/**
 * Set D W2 N2 — stale-bundle nudge.
 *
 * obs7's N2 finding: an SPA already loaded never re-requests its JS, so after
 * the Set B deploy the clinic ran the OLD bundle for TWO FULL CLINIC DAYS —
 * every client-side fix inert, and the bundle census read "zero stale-hash
 * requests" precisely BECAUSE a stale client requests nothing. The remedy until
 * now was a WhatsApp message asking for a hard reload.
 *
 * DESIGN RULES (deliberate):
 *  - NEVER auto-reload. Losing a half-finished assessment is worse than running
 *    a stale client for another hour.
 *  - Non-blocking: corner card, nothing modal, the app stays fully usable.
 *  - PERSISTENT until dismissed, with a Reload action. This is a disclosed
 *    deviation from the brief's word "toast": the toast bus here auto-dismisses
 *    after a few seconds and carries no button, and a nudge a clinician can
 *    miss mid-consultation is the exact failure we are fixing.
 *  - At most once an hour, and dismissing suppresses it for an hour.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { runningBundle, readSuppressedUntil, suppressForAnHour, shouldNudge, POLL_MS } from '@/lib/versionNudge';

export default function VersionNudge(): JSX.Element | null {
  const { t } = useTranslation();
  const [stale, setStale] = useState(false);

  const check = useCallback(async () => {
    const suppressedUntil = readSuppressedUntil();
    if (Date.now() < suppressedUntil) return;
    const mine = runningBundle();
    if (!mine) return; // dev server / no hashed bundle — nothing to compare
    try {
      // lib/api.ts, not a raw fetch — the BR-09 ratchet exists to stop raw /api
      // calls drifting back in. /api/version is on the NO_SILENT_REFRESH list
      // there, so this poll never triggers background auth churn.
      const res = await api.get<{ bundle?: string | null }>('/version');
      const served = res.success ? res.data?.bundle : null;
      if (shouldNudge({ running: mine, served, suppressedUntil, now: Date.now() })) setStale(true);
    } catch { /* offline or blocked — silence is correct, never nag on a failure */ }
  }, []);

  useEffect(() => {
    void check();
    const onFocus = (): void => { void check(); };
    window.addEventListener('focus', onFocus);
    const id = window.setInterval(() => { void check(); }, POLL_MS);
    return () => { window.removeEventListener('focus', onFocus); window.clearInterval(id); };
  }, [check]);

  if (!stale) return null;

  return (
    <div
      data-testid="version-nudge"
      role="status"
      className="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border border-teal-300 bg-white shadow-lg p-3 flex items-start gap-3"
    >
      <span className="text-xl leading-none">🔄</span>
      <div className="flex-1">
        <p className="text-sm text-gray-800">
          {t('versionNudge.message', 'A new version is available')}
        </p>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            data-testid="version-nudge-reload"
            onClick={() => window.location.reload()}
            className="text-xs font-medium px-2.5 py-1 rounded bg-teal-600 text-white hover:bg-teal-700"
          >
            {t('versionNudge.reload', 'Reload')}
          </button>
          <button
            type="button"
            data-testid="version-nudge-dismiss"
            onClick={() => { suppressForAnHour(); setStale(false); }}
            className="text-xs font-medium px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            {t('versionNudge.later', 'Later')}
          </button>
        </div>
      </div>
    </div>
  );
}
